import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useCallback, useState } from 'react';

import { useReelFeed } from '@/hooks/useReelFeed';

import { ReelSlide } from './ReelSlide';
import { type ViewerVideo } from './types';
import { ViewerIconButton } from './ViewerIconButton';

type Props = {
  videos: readonly ViewerVideo[];
  initialIndex: number;
  onActiveChange?: ((index: number) => void) | undefined;
};

/**
 * The scroller: every clip stacked, one screen each.
 *
 * Mounted only while the viewer is open, which is what lets `initialIndex` be
 * read once (see `useReelFeed`) — the dialog unmounts its children on close,
 * so "the index at mount" and "the index this opened at" are the same thing.
 *
 * ## Muting is the feed's, not the slide's
 *
 * One switch for the whole feed. A visitor who turns the sound on has decided
 * they want sound, and having to turn it on again at every clip is the sort
 * of thing that makes somebody close the tab. It lives here rather than in
 * the viewer above because nothing outside the feed needs to know about it.
 *
 * The "swipe" hint is handed to the first slide rather than drawn here: it
 * has to stack above that slide's caption, and the caption lives in the slide.
 *
 * ## Arrows on desktop only
 *
 * A mouse has no swipe. The pair at the right edge is how a desktop visitor
 * moves between clips without discovering that the wheel happens to work;
 * on a phone they would be two buttons covering the video to do what the
 * thumb already does better, so they are not drawn at all.
 */
export function ReelFeed({ videos, initialIndex, onActiveChange }: Props) {
  const [muted, setMuted] = useState(true);
  const feed = useReelFeed({ count: videos.length, initialIndex, onActiveChange });
  const toggleMute = useCallback(() => setMuted((on) => !on), []);

  return (
    <>
      <Box
        component="ol"
        ref={feed.scrollerRef}
        tabIndex={-1}
        aria-label="Delivery videos"
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          height: '100svh',
          overflowY: 'auto',
          overflowX: 'hidden',
          // Mandatory, so the feed can never rest showing half of two clips.
          scrollSnapType: 'y mandatory',
          // Stops a flick at the first or last clip from turning into the
          // browser's own pull-to-refresh or back gesture.
          overscrollBehavior: 'contain',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {videos.map((video, index) => (
          <Box
            component="li"
            key={video.id}
            aria-current={index === feed.active}
            sx={{ height: '100%', width: '100%' }}
          >
            <ReelSlide
              video={video}
              active={index === feed.active}
              loaded={feed.shouldLoad(index)}
              muted={muted}
              onToggleMute={toggleMute}
              position={`${index + 1} / ${videos.length}`}
              {...(index === 0 && videos.length > 1 ? { hint: 'Swipe for more' } : {})}
            />
          </Box>
        ))}
      </Box>

      {videos.length > 1 && (
        <Stack
          spacing={3}
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'absolute',
            right: 24,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 3,
          }}
        >
          <ViewerIconButton
            label="Previous video"
            icon={<KeyboardArrowUp />}
            size={56}
            disabled={feed.active === 0}
            onClick={feed.previous}
          />
          <ViewerIconButton
            label="Next video"
            icon={<KeyboardArrowDown />}
            size={56}
            disabled={feed.active === videos.length - 1}
            onClick={feed.next}
          />
        </Stack>
      )}

    </>
  );
}
